import { useEffect } from "react";
import { useAuth } from "../../context/AuthContext";

const FEEDBACK_SCRIPT_ID = "mediatropy-feedback-widget";
const DEFAULT_FEEDBACK_WIDGET_SRC = "https://feedback.mediatropy.com/widget.js";
const PRODUCTION_HOSTNAME = "hrp.mediatropy.com";

function normalizeBooleanEnv(value, fallback = false) {
    if (value === undefined || value === null || value === "") return fallback;
    return ["1", "true", "yes", "on"].includes(String(value).trim().toLowerCase());
}

function getFeedbackConfig(hostname) {
    const projectToken = import.meta.env.VITE_FEEDBACK_PROJECT_TOKEN || "";
    const widgetUrl = import.meta.env.VITE_FEEDBACK_WIDGET_URL || DEFAULT_FEEDBACK_WIDGET_SRC;
    const enabled = normalizeBooleanEnv(import.meta.env.VITE_FEEDBACK_WIDGET_ENABLED, Boolean(projectToken));
    const devMode = normalizeBooleanEnv(
        import.meta.env.VITE_FEEDBACK_DEV_MODE,
        shouldUseFeedbackDevMode(hostname)
    );

    return { enabled, projectToken, devMode, widgetUrl };
}

export function shouldUseFeedbackDevMode(hostname) {
    if (!hostname) return true;
    return hostname.toLowerCase() !== PRODUCTION_HOSTNAME;
}

function removeFeedbackScript() {
    const existingScript = document.getElementById(FEEDBACK_SCRIPT_ID);
    if (existingScript) {
        existingScript.remove();
    }
}

function createFeedbackScript(email, config) {
    const script = document.createElement("script");
    script.id = FEEDBACK_SCRIPT_ID;
    script.src = config.widgetUrl;
    script.async = true;
    script.dataset.projectToken = config.projectToken;
    script.dataset.userEmail = email;

    if (config.devMode) {
        script.dataset.devMode = "true";
    }

    return script;
}

function FeedbackWidgetLoader() {
    const auth = useAuth();
    const email = auth?.user?.email;

    useEffect(() => {
        if (typeof document === "undefined") return undefined;

        if (!email) {
            removeFeedbackScript();
            return undefined;
        }

        const hostname = window.location?.hostname;
        const config = getFeedbackConfig(hostname);
        if (!config.enabled || !config.projectToken) {
            removeFeedbackScript();
            return undefined;
        }

        const existingScript = document.getElementById(FEEDBACK_SCRIPT_ID);
        const expectedDevMode = config.devMode ? "true" : undefined;

        if (
            existingScript?.dataset?.userEmail === email &&
            existingScript?.dataset?.projectToken === config.projectToken &&
            existingScript?.dataset?.devMode === expectedDevMode
        ) {
            return undefined;
        }

        removeFeedbackScript();
        document.body.appendChild(createFeedbackScript(email, config));

        return undefined;
    }, [email]);

    return null;
}

export default FeedbackWidgetLoader;
